import type { SelectProps } from 'antd';
import { Balloon } from '../../interfaces/Ballooning';
import { GageType, KeyenceSystem } from '../../interfaces/Quality.Interface';

/** Which measurement fields apply to a balloon; shared by the balloon table and the quick edit. */

export const KEYENCE_VIEWS = ['Top', 'Front', 'Back', 'Left', 'Right', 'Bottom', 'Custom 1', 'Custom 2'];
export const VISUAL_TYPES = ['Engraving', 'Surface Finish', 'Other'];

export interface StrategyOption {
	value: string;
	keyenceId?: string;
}

export type MeasurementField =
	| 'view'
	| 'measurement_type'
	| 'gage_type'
	| 'text_value'
	| 'nominal'
	| 'tolerance_minus'
	| 'tolerance_plus';

export const isKeyenceStrategy = (strategy: string | null | undefined) => strategy === 'Keyence';

const isThreadGage = (gage: GageType | undefined) => gage?.category?.name === 'Thread Gages';

/** Fields the balloon's strategy, type and gage leave no meaning for. */
export const isFieldDisabled = (
	field: MeasurementField,
	balloon: Balloon,
	gages: Record<string, GageType>
): boolean => {
	const strategy = balloon.measurement_strategy;
	const type = balloon.measurement_type;
	const noNominal =
		type === 'Engraving' ||
		isThreadGage(gages[balloon.gage_type]) ||
		type === 'Surface Finish' ||
		type === 'Other';
	switch (field) {
		case 'view':
			return !isKeyenceStrategy(strategy);
		case 'measurement_type':
			return isKeyenceStrategy(strategy);
		case 'gage_type':
			return isKeyenceStrategy(strategy) || type === 'Engraving';
		case 'text_value':
			return type === 'Dimension' || isKeyenceStrategy(strategy);
		default:
			return noNominal;
	}
};

export type MissingField = { field: keyof Balloon; label: string };

const isBlank = (value: unknown) =>
	value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const isNumber = (value: unknown) =>
	!isBlank(value) && /\d/.test(String(value)) && Number.isFinite(Number(value));

/**
 * What a balloon still needs before ballooning can be completed: a strategy, its tool,
 * and a nominal with both tolerances or, for text checks, the text.
 * Mirrors ballooning/services/completion.py.
 */
export const missingFields = (
	balloon: Balloon,
	gages: Record<string, GageType>
): MissingField[] => {
	const strategy = balloon.measurement_strategy;
	if (!strategy) return [{ field: 'measurement_strategy', label: 'strategy' }];

	const missing: MissingField[] = [];
	if (isKeyenceStrategy(strategy) && !balloon.view) {
		missing.push({ field: 'view', label: 'view' });
	}
	if (strategy === 'Hand Gage' && !balloon.gage_type) {
		missing.push({ field: 'gage_type', label: 'gage' });
	}
	// Every visual type is a text check, so the text is only asked for once there is one.
	if (strategy === 'Visual' && !balloon.measurement_type) {
		return [{ field: 'measurement_type', label: 'type' }];
	}

	if (!isFieldDisabled('nominal', balloon, gages)) {
		if (!isNumber(balloon.nominal)) missing.push({ field: 'nominal', label: 'nominal' });
		if (!isNumber(balloon.tolerance_minus)) {
			missing.push({ field: 'tolerance_minus', label: 'minus tolerance' });
		}
		if (!isNumber(balloon.tolerance_plus)) {
			missing.push({ field: 'tolerance_plus', label: 'plus tolerance' });
		}
	} else if (!isFieldDisabled('text_value', balloon, gages) && isBlank(balloon.text_value)) {
		missing.push({ field: 'text_value', label: 'description' });
	}
	return missing;
};

/** Keyence devices usable at the part's building; devices with no building always count. */
export const reachableKeyenceSystems = (
	systems: KeyenceSystem[] | undefined,
	buildingId: string | null
): KeyenceSystem[] | undefined => {
	if (!systems || !buildingId) return systems;
	const reachable = systems.filter((s) => !s.building || s.building.id === buildingId);
	// Never filter down to nothing, or the balloonner cannot pick a device at all.
	return reachable.length > 0 ? reachable : systems;
};

export const strategyOptions = (systems: KeyenceSystem[] | undefined): StrategyOption[] => [
	...(systems && systems.length > 0
		? systems.map((s) => ({ value: s.name, keyenceId: s.id }))
		: [{ value: 'Keyence' }]),
	{ value: 'Hand Gage' },
	{ value: 'Visual' },
];

/** The strategy picker's value: a Keyence balloon shows its device. */
export const strategyValue = (balloon: Balloon, systems: KeyenceSystem[] | undefined) =>
	isKeyenceStrategy(balloon.measurement_strategy)
		? systems?.find((s) => s.id === balloon.keyence_device)?.name ||
			balloon.keyence_device_name ||
			'Keyence'
		: balloon.measurement_strategy;

export const strategyUpdates = (value: string, option: StrategyOption): Partial<Balloon> =>
	option.keyenceId
		? { measurement_strategy: 'Keyence', keyence_device: option.keyenceId }
		: { measurement_strategy: value, keyence_device: null };

export const gageGroup = (gage: GageType) =>
	gage.category?.name?.includes('Thread') ? 'Thread' : 'Dimension';

type ToolOption = { value: string; label: string };
type ToolOptions = NonNullable<SelectProps['options']>;

/**
 * The "Tool" picker depends on the strategy: a Keyence view, a hand gage, or what a visual
 * check looks at. `options` is grouped for hand gages.
 */
export const toolField = (
	balloon: Balloon,
	fastlaneGages: GageType[]
): {
	label: string;
	placeholder: string;
	value: string;
	options: ToolOptions;
} | null => {
	const strategy = balloon.measurement_strategy;
	if (isKeyenceStrategy(strategy)) {
		return {
			label: 'View',
			placeholder: 'Select view...',
			value: balloon.view || '',
			options: KEYENCE_VIEWS.map((view) => ({ value: view, label: view })),
		};
	}
	if (strategy === 'Hand Gage') {
		const groups: Record<string, ToolOption[]> = {};
		fastlaneGages.forEach((gage) => {
			(groups[gageGroup(gage)] ??= []).push({ value: gage.id, label: gage.name });
		});
		return {
			label: 'Gage',
			placeholder: 'Select gage...',
			value: fastlaneGages.some((g) => g.id === balloon.gage_type) ? balloon.gage_type : '',
			options: Object.entries(groups).map(([group, options]) => ({
				label: `${group} Gages`,
				options,
			})),
		};
	}
	if (strategy === 'Visual') {
		return {
			label: 'Type',
			placeholder: 'Select type...',
			value: balloon.measurement_type || '',
			options: VISUAL_TYPES.map((type) => ({ value: type, label: type })),
		};
	}
	return null;
};

/** What picking `value` in the tool picker changes on the balloon, or null for nothing. */
export const toolUpdates = (
	balloon: Balloon,
	value: string,
	fastlaneGages: GageType[]
): Partial<Balloon> | null => {
	const strategy = balloon.measurement_strategy;
	if (isKeyenceStrategy(strategy)) return { view: value };
	if (strategy === 'Hand Gage') {
		const gage = fastlaneGages.find((g) => g.id === value);
		return gage ? { measurement_type: gageGroup(gage), gage_type: value } : null;
	}
	if (strategy === 'Visual') return { measurement_type: value };
	return null;
};
